import {
    ChangeDetectionStrategy,
    Component,
    computed,
    HostListener,
    input,
    output,
    signal,
} from '@angular/core';
import {TranslatePipe} from '@brunata-angular/util';
import {StepLimitComponent} from './step-limit.component';
import {StepMenuItem} from './step-menu.model';

@Component({
    selector: 'app-step-menu',
    standalone: true,
    imports: [TranslatePipe, StepLimitComponent],
    template: `
        <nav
            class="border bg-white"
            [attr.aria-label]="
                'quickexports_export_step_menu_label' | translate: 'online' : 'export'
            ">
            <div class="border-bottom p-3">
                <h2 class="fs-5 fw-bold mb-0">
                    {{ headingKey() | translate: 'online' : 'export' }}
                </h2>
            </div>

            <ol class="list-unstyled mb-0 py-2">
                @for (item of rows(); track item.id) {
                    <li class="step-row">
                        @if (!$last) {
                            <span
                                class="step-line-fill"
                                aria-hidden="true"
                                [style.--travel]="item.state.travel"></span>
                        }
                        <div class="d-flex align-items-center">
                            <button
                                type="button"
                                class="step-link"
                                [class.current]="item.state.place === 'current'"
                                [class.invalid]="item.state.status === 'invalid'"
                                [attr.aria-current]="item.id === currentId() ? 'step' : null"
                                (click)="stepSelected.emit(item.id)">
                                <span
                                    class="step-number step-badge"
                                    aria-hidden="true"
                                    [class.current]="item.state.place === 'current'"
                                    [class.passed]="item.state.place === 'passed'"
                                    [class.empty]="item.state.status === 'empty'"
                                    [class.invalid]="item.state.status === 'invalid'">
                                    {{ item.stepNumber }}
                                </span>
                                <span class="step-title text-truncate">
                                    {{ item.titleKey | translate: 'online' : 'export' }}
                                </span>
                            </button>
                            @if (item.limit !== null) {
                                <button
                                    type="button"
                                    class="limit-button"
                                    [class.open]="item.open"
                                    [attr.aria-expanded]="item.open"
                                    [attr.aria-controls]="item.open ? item.popoverId : null"
                                    [attr.aria-label]="
                                        'customizedexports_export_limits_step_button'
                                            | translate: 'online' : 'export'
                                    "
                                    [title]="
                                        'customizedexports_export_limits_step_button'
                                            | translate: 'online' : 'export'
                                    "
                                    (click)="toggleLimit(item.id)">
                                    <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
                                </button>
                            }
                        </div>
                        @if (item.limit; as limit) {
                            @if (item.open) {
                                <div
                                    class="limit-popover"
                                    role="region"
                                    [id]="item.popoverId"
                                    [attr.aria-label]="
                                        limit.headline.key
                                            | translate: 'online' : 'export' : limit.headline.params
                                    ">
                                    <button
                                        type="button"
                                        class="btn-close limit-close"
                                        [attr.aria-label]="
                                            'customizedexports_export_limits_close'
                                                | translate: 'online' : 'export'
                                        "
                                        (click)="closeLimit()"></button>
                                    <app-step-limit [limit]="limit" />
                                </div>
                            }
                        }
                    </li>
                }
            </ol>
        </nav>
    `,
    styles: `
        :host {
            --limit-popover-background: color-mix(in srgb, var(--bs-danger) 3%, var(--bs-white));
            display: block;
            flex: 0 0 18.75rem;
            min-width: 0;
            margin-bottom: 1.5rem;
        }

        @media (min-width: 992px) {
            :host {
                position: sticky;
                top: var(--step-sticky-offset, 1rem);
                z-index: 3;
                margin-bottom: 0;
            }
        }

        .step-row {
            position: relative;
        }

        .step-row:not(:last-child)::after {
            content: '';
            position: absolute;
            top: 2.375rem;
            bottom: -0.625rem;
            left: 2.125rem;
            width: 1px;
            background-color: var(--bs-border-color);
        }

        .step-link {
            display: flex;
            flex: 1 1 auto;
            align-items: center;
            gap: 0.75rem;
            min-width: 0;
            height: 3rem;
            padding: 0 1.25rem;
            border: 0;
            background: none;
            color: var(--bs-body-color);
            text-align: start;
        }

        .step-link:hover {
            background-color: var(--brunata-blue-faint);
        }

        .step-link.current {
            color: var(--bs-primary);
            font-weight: 700;
        }

        .step-number {
            position: relative;
            z-index: 1;
            font-size: 0.875rem;
        }

        .step-link.invalid {
            color: var(--bs-danger);
        }

        .step-line-fill {
            position: absolute;
            z-index: 1;
            top: 2.375rem;
            bottom: -0.625rem;
            left: calc(2.125rem - 1px);
            width: 3px;
            background-color: var(--bs-primary);
            transform: scaleY(var(--travel));
            transform-origin: top;
            pointer-events: none;
        }

        .step-title {
            min-width: 0;
            font-size: 0.9375rem;
        }

        .limit-button {
            display: inline-flex;
            flex: none;
            align-items: center;
            justify-content: center;
            width: 1.75rem;
            height: 1.75rem;
            margin-right: 1rem;
            padding: 0;
            border: 1px solid rgba(var(--bs-danger-rgb), 0.45);
            background-color: var(--bs-white);
            color: var(--bs-danger);
        }

        .limit-button:hover {
            border-color: var(--bs-danger);
        }

        .limit-button.open {
            border-color: var(--bs-danger);
            background-color: var(--bs-danger);
            color: var(--bs-white);
        }

        .limit-popover {
            position: relative;
            z-index: 2;
            margin: 0 1rem 0.75rem;
            padding: 0.75rem 2.75rem 0.75rem 1rem;
            border: 1px solid rgba(var(--bs-danger-rgb), 0.6);
            background-color: var(--limit-popover-background);
            font-size: 0.875rem;
        }

        .limit-close {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
        }

        @media (min-width: 992px) {
            .limit-popover {
                position: absolute;
                top: 0.375rem;
                left: calc(100% + 0.75rem);
                z-index: 5;
                width: 22.5rem;
                margin: 0;
            }

            .limit-popover::before {
                content: '';
                position: absolute;
                top: 0.75rem;
                left: -0.4375rem;
                width: 0.75rem;
                height: 0.75rem;
                border-bottom: 1px solid rgba(var(--bs-danger-rgb), 0.6);
                border-left: 1px solid rgba(var(--bs-danger-rgb), 0.6);
                background-color: var(--limit-popover-background);
                transform: rotate(45deg);
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepMenuComponent {
    readonly headingKey = input.required<string>();
    readonly items = input.required<ReadonlyArray<StepMenuItem>>();
    readonly currentId = input.required<string | null>();

    readonly stepSelected = output<string>();

    protected readonly openLimitId = signal<string | null>(null);

    protected readonly rows = computed(() =>
        this.items().map(item => ({
            ...item,
            popoverId: `step-limit-${item.id}`,
            open: item.id === this.openLimitId(),
        })),
    );

    protected toggleLimit(stepId: string): void {
        this.openLimitId.update(openId => (openId === stepId ? null : stepId));
    }

    @HostListener('keydown.escape')
    protected closeLimit(): void {
        this.openLimitId.set(null);
    }
}
